import { getSafeAreaInsets } from "@apps-in-toss/web-framework";

// UI 관리 클래스
class UIManager {
  constructor() {
    this.elements = {};
    this.gameStartTime = null; // 게임 시작 시간
    this.isGameRunning = false; // 게임 실행 상태
    this.pausedTime = 0; // 일시정지된 총 시간 (밀리초)
    this.pauseStartTime = null; // 일시정지 시작 시간
    this.isUpgradeSelectionOpen = false; // 업그레이드 선택 화면 열림 상태
    // 한국어 주석: 안전 영역 업데이트에서 사용할 바인딩된 핸들러 사전 준비
    this.boundSafeAreaUpdate = this.applySafeAreaInsets.bind(this);
    this.initializeElements();
    // 한국어 주석: 초기화 직후 안전 영역 값을 반영하여 UI를 재배치
    this.applySafeAreaInsets();
    this.setupSafeAreaWatcher();
  }

  // UI 요소들 초기화
  initializeElements() {
    // 기존 요소들
    this.elements.pointsDisplay = document.getElementById("pointsDisplay");
    this.elements.gameTimeDisplay = document.getElementById("gameTimeDisplay");

    // 새로운 UI 요소들
    this.elements.pauseButton = document.getElementById("pauseButton");
    this.elements.skeletonCount = document.getElementById("skeletonCount");
    this.elements.levelDisplay = document.getElementById("levelDisplay");
    this.elements.progressFill = document.getElementById("progressFill");
    this.elements.screenEffectOverlay = document.getElementById(
      "screenEffectOverlay"
    );
    this.elements.topUIBar = document.getElementById("topUIBar");
    this.elements.progressContainer = document.getElementById(
      "progressContainer"
    );

    // 요소 존재 확인
    if (!this.elements.pointsDisplay) {
      console.warn("포인트 표시 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.gameTimeDisplay) {
      console.warn("게임 시간 표시 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.pauseButton) {
      console.warn("일시정지 버튼 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.skeletonCount) {
      console.warn("해골 카운트 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.levelDisplay) {
      console.warn("레벨 표시 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.progressFill) {
      console.warn("진행도 바 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.screenEffectOverlay) {
      console.warn("화면 효과 오버레이 UI 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.topUIBar) {
      console.warn("상단 UI 바 요소를 찾을 수 없습니다.");
    }
    if (!this.elements.progressContainer) {
      console.warn("진행도 컨테이너 요소를 찾을 수 없습니다.");
    }

    // 일시정지 버튼 이벤트 리스너 설정
    this.setupPauseButton();
  }

  // 한국어 주석: 기기 안전 영역 정보를 이용해 상단 UI를 안전하게 배치
  applySafeAreaInsets() {
    try {
      const insets = getSafeAreaInsets();
      const safeTop = insets?.top ?? 0;

      if (typeof document !== "undefined") {
        document.documentElement.style.setProperty(
          "--safe-area-top",
          `${safeTop}px`
        );
      }

      if (this.elements.topUIBar) {
        this.elements.topUIBar.style.top = `${safeTop}px`;
      }

      if (this.elements.progressContainer) {
        const baseOffset = 50;
        this.elements.progressContainer.style.top = `${baseOffset + safeTop}px`;
      }
    } catch (error) {
      console.warn("안전 영역 값을 적용하는 중 문제가 발생했습니다.", error);
    }
  }

  // 한국어 주석: 회전이나 창 크기 변경 시 안전 영역을 재적용하도록 감시
  setupSafeAreaWatcher() {
    if (typeof window === "undefined") {
      return;
    }

    window.addEventListener("resize", this.boundSafeAreaUpdate);
    window.addEventListener("orientationchange", this.boundSafeAreaUpdate);
  }

  // 포인트 업데이트
  updatePoints(points) {
    if (this.elements.pointsDisplay) {
      this.elements.pointsDisplay.textContent = `${points}`;
    }
  }

  // 체력 표시 (추후 확장 가능)
  updateHealth(health, maxHealth) {
    // 추후 체력 바 UI가 추가되면 여기서 업데이트
    Utils.log(`체력: ${health}/${maxHealth}`);
  }

  // UI 요소 표시/숨김
  showElement(elementName) {
    if (this.elements[elementName]) {
      this.elements[elementName].style.display = "block";
    }
  }

  hideElement(elementName) {
    if (this.elements[elementName]) {
      this.elements[elementName].style.display = "none";
    }
  }

  // 게임 시간 관련 메서드들

  // 게임 시작 시간 설정
  startGameTimer() {
    // 이미 게임이 시작된 적이 있다면 기존 시간을 유지
    if (!this.gameStartTime) {
      this.gameStartTime = Date.now();
    }
    this.isGameRunning = true;
    this.pauseStartTime = null; // 일시정지 시간 리셋
    Utils.log("게임 타이머 시작");
  }

  // 게임 시간 업데이트 (매 프레임 호출)
  updateGameTime() {
    if (
      !this.isGameRunning ||
      !this.gameStartTime ||
      !this.elements.gameTimeDisplay
    ) {
      return;
    }

    const currentTime = Date.now();
    // 일시정지된 시간을 제외한 실제 게임 시간 계산
    const elapsedTime = currentTime - this.gameStartTime - this.pausedTime;

    // 시간을 분:초 형식으로 변환
    const minutes = Math.floor(elapsedTime / 60000);
    const seconds = Math.floor((elapsedTime % 60000) / 1000);

    // 시간 표시 업데이트 (00:00 형식)
    const timeString = `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
    this.elements.gameTimeDisplay.textContent = timeString;
  }

  // 게임 타이머 정지 (일시정지)
  stopGameTimer() {
    if (this.isGameRunning) {
      this.pauseStartTime = Date.now(); // 일시정지 시작 시간 기록
      this.isGameRunning = false;
      Utils.log("게임 타이머 일시정지");
    }
  }

  // 게임 타이머 재개
  resumeGameTimer() {
    if (!this.isGameRunning && this.pauseStartTime) {
      // 일시정지된 시간을 누적
      this.pausedTime += Date.now() - this.pauseStartTime;
      this.pauseStartTime = null;
      this.isGameRunning = true;
      Utils.log("게임 타이머 재개");
    }
  }

  // 게임 타이머 리셋
  resetGameTimer() {
    this.gameStartTime = null;
    this.isGameRunning = false;
    this.pausedTime = 0; // 일시정지 시간도 리셋
    this.pauseStartTime = null;
    if (this.elements.gameTimeDisplay) {
      this.elements.gameTimeDisplay.textContent = "00:00";
    }
    Utils.log("게임 타이머 리셋");
  }

  // 현재 게임 시간 가져오기 (초 단위)
  getGameTime() {
    if (!this.gameStartTime) {
      return 0;
    }
    // 일시정지된 시간을 제외한 실제 게임 시간 반환
    return (Date.now() - this.gameStartTime - this.pausedTime) / 1000;
  }

  // UI 전체 표시/숨김
  showUI() {
    const gameUI = document.getElementById("gameUI");
    if (gameUI) {
      gameUI.style.display = "block";
    }
  }

  hideUI() {
    const gameUI = document.getElementById("gameUI");
    if (gameUI) {
      gameUI.style.display = "none";
    }
  }

  // === 새로운 UI 메서드들 ===

  // 일시정지 버튼 설정
  setupPauseButton() {
    if (!this.elements.pauseButton) return;

    this.elements.pauseButton.addEventListener("click", () => {
      this.togglePause();
    });
  }

  // 일시정지 토글
  togglePause() {
    const game = window.game;
    if (!game) return;

    const isPaused = game.gameLoop ? game.gameLoop.getStatus().isPaused : false;

    if (isPaused) {
      // 현재 일시정지 상태 -> 재개
      game.resume();
      this.elements.pauseButton.classList.remove("paused");
    } else {
      // 현재 실행 중 -> 일시정지
      game.pause();
      this.elements.pauseButton.classList.add("paused");
    }
  }

  // 해골 카운트 업데이트
  updateSkeletonCount(count) {
    if (this.elements.skeletonCount) {
      this.elements.skeletonCount.textContent = count;
    }
  }

  // 레벨 정보 업데이트
  updateLevelInfo(levelInfo) {
    if (!levelInfo) return;

    // 레벨 표시 업데이트
    if (this.elements.levelDisplay) {
      this.elements.levelDisplay.textContent = levelInfo.level;
    }

    // 연속적인 진행도 바 업데이트
    this.updateProgressBar(levelInfo);
  }

  // 연속적인 진행도 바 업데이트
  updateProgressBar(levelInfo) {
    if (!this.elements.progressFill || !levelInfo) return;

    const progressPercentage = levelInfo.progressPercentage || 0;
    this.elements.progressFill.style.width = `${progressPercentage}%`;

    // 레벨바가 100%에 도달하고 레벨업 대기 상태일 때 레벨업 처리
    if (progressPercentage >= 100 && levelInfo.isLevelUpPending) {
      // 짧은 delay 후 레벨업 처리 (연속 레벨업 시 부드러운 처리) - 레벨업 후 지연시간 줄이기
      setTimeout(() => {
        if (
          window.game &&
          window.game.player &&
          window.game.player.processLevelUp
        ) {
          window.game.player.processLevelUp();
        }
      }, 100);
    }
  }

  // 플레이어 정보 업데이트 (통합 메서드)
  updatePlayerInfo(player) {
    if (!player) return;

    // 포인트 업데이트
    this.updatePoints(player.points);

    // 레벨 정보 업데이트
    const levelInfo = player.getLevelInfo();
    this.updateLevelInfo(levelInfo);
    this.updateSkeletonCount(levelInfo.killedSkeletons);
  }

  // === 게임 종료 화면 관련 메서드들 ===

  // 게임 종료 화면 표시
  showGameOverScreen(playerInfo) {
    // 게임 종료 화면 HTML 생성
    this.createGameOverScreen(playerInfo);

    // 기존 게임 UI 숨기기
    this.hideUI();

    // 게임 종료 화면 표시
    const gameOverScreen = document.getElementById("gameOverScreen");
    if (gameOverScreen) {
      gameOverScreen.classList.add("show");
    }

    Utils.log("게임 종료 화면이 표시되었습니다.");
  }

  // 게임 종료 화면 숨기기
  hideGameOverScreen() {
    const gameOverScreen = document.getElementById("gameOverScreen");
    if (gameOverScreen) {
      gameOverScreen.classList.remove("show");
    }

    // 게임 UI 다시 표시
    this.showUI();

    Utils.log("게임 종료 화면이 숨겨졌습니다.");
  }

  // 게임 종료 화면 HTML 생성
  createGameOverScreen(playerInfo) {
    // 기존 게임 종료 화면이 있으면 제거
    const existingScreen = document.getElementById("gameOverScreen");
    if (existingScreen) {
      existingScreen.remove();
    }

    // 시간을 분:초 형식으로 변환
    const minutes = Math.floor(playerInfo.survivalTime / 60);
    const seconds = Math.floor(playerInfo.survivalTime % 60);
    const timeString = `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;

    // 게임 종료 화면 HTML 생성
    const gameOverHTML = `
      <div id="gameOverScreen" class="game-over-screen">
        <div class="game-over-content">
          <h1 class="game-over-title">게임 종료</h1>
          
          <div class="game-over-content-container">
            <div class="game-over-stats">
            <div class="stat-item">
            <span class="stat-label">버틴 시간</span>
            <span class="stat-value">${timeString}</span>
            </div>
            <div class="stat-item">
            <span class="stat-label">달성 레벨</span>
            <span class="stat-value">Lv.${playerInfo.level}</span>
            </div>
            <div class="stat-item">
            <span class="stat-label">처치 해골 수</span>
            <span class="stat-value">${playerInfo.killCount}마리</span>
            </div>
            </div>
          </div>

          <div class="game-over-buttons">
            <button id="shareButton" class="game-over-button share-button">
              📤 공유하기
            </button>
            <button id="restartButton" class="game-over-button restart-button">
              🔄 다시하기
            </button>
          </div>
        </div>
      </div>
    `;

    // HTML에 추가
    document.body.insertAdjacentHTML("beforeend", gameOverHTML);

    // 버튼 이벤트 리스너 설정
    this.setupGameOverButtons();
  }

  // 게임 종료 화면 버튼 이벤트 설정
  setupGameOverButtons() {
    // 공유하기 버튼
    const shareButton = document.getElementById("shareButton");
    if (shareButton) {
      shareButton.addEventListener("click", () => {
        this.shareGameResult();
      });
    }

    // 다시하기 버튼
    const restartButton = document.getElementById("restartButton");
    if (restartButton) {
      restartButton.addEventListener("click", () => {
        this.restartGame();
      });
    }
  }

  // 게임 결과 공유
  shareGameResult() {
    const gameOverScreen = document.getElementById("gameOverScreen");
    if (!gameOverScreen) return;

    // 게임 결과 정보 수집
    const timeElement = gameOverScreen.querySelector(
      ".stat-item:nth-child(1) .stat-value"
    );
    const levelElement = gameOverScreen.querySelector(
      ".stat-item:nth-child(2) .stat-value"
    );
    const killElement = gameOverScreen.querySelector(
      ".stat-item:nth-child(3) .stat-value"
    );

    const time = timeElement ? timeElement.textContent : "00:00";
    const level = levelElement ? levelElement.textContent : "Lv.1";
    const kills = killElement ? killElement.textContent : "0마리";

    // 공유할 텍스트 생성
    const shareTitle = "⚔️ 싸우자해골아 💀\n\n";
    const shareText = `⏰ 버틴 시간: ${time}\n📈 달성 레벨: ${level}\n💀 처치 해골: ${kills}\n\n`;
    const shareUrl = "https://minion.toss.im/3nSzQ9ac";

    // Web Share API 지원 여부 확인 (한국어 주석: 브라우저의 네이티브 공유 기능 사용)
    if (navigator.share) {
      // Web Share API 사용 (모바일에서 네이티브 공유 UI 띄움)
      navigator
        .share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        })
        .then(() => {
          Utils.log("게임 결과가 공유되었습니다.");
          this.showShareSuccess();
        })
        .catch((error) => {
          // 사용자가 공유를 취소한 경우는 에러가 아님
          if (error.name !== "AbortError") {
            Utils.log("Web Share API 실패, 클립보드 복사로 전환: " + error);
            this.fallbackToClipboard(shareTitle, shareText, shareUrl);
          }
        });
    } else {
      // Web Share API를 지원하지 않으면 클립보드 복사 (한국어 주석: 데스크톱 브라우저 대응)
      this.fallbackToClipboard(shareTitle, shareText, shareUrl);
    }
  }

  // 클립보드 복사로 fallback (한국어 주석: Web Share API 미지원 시 대체 방법)
  fallbackToClipboard(title, text, url) {
    const fullText = `${title}\n\n${text}\n\n${url}`;

    // 클립보드에 복사
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(fullText)
        .then(() => {
          this.showShareSuccess("클립보드에 복사됨!");
        })
        .catch(() => {
          this.showShareFallback(fullText);
        });
    } else {
      this.showShareFallback(fullText);
    }
  }

  // 공유 성공 메시지 표시 (한국어 주석: 공유 또는 복사 성공 시 사용자 피드백)
  showShareSuccess(message = "✅ 공유됨!") {
    const shareButton = document.getElementById("shareButton");
    if (shareButton) {
      const originalText = shareButton.innerHTML;
      shareButton.innerHTML = message;
      shareButton.style.background =
        "linear-gradient(135deg, #4CAF50, #45a049)";

      setTimeout(() => {
        shareButton.innerHTML = originalText;
        shareButton.style.background = "";
      }, 2000);
    }
  }

  // 공유 실패 시 대체 방법 제공
  showShareFallback(shareText) {
    // 텍스트 영역을 생성하여 사용자가 수동으로 복사할 수 있도록 함
    const textArea = document.createElement("textarea");
    textArea.value = shareText;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      this.showShareSuccess();
    } catch (err) {
      // 복사 실패 시 텍스트를 알림으로 표시
      alert("게임 결과:\n\n" + shareText);
    }

    document.body.removeChild(textArea);
  }

  // 게임 재시작
  restartGame() {
    // Game 인스턴스가 전역에 있다고 가정
    if (window.game && window.game.restartGame) {
      window.game.restartGame();
    } else {
      Utils.log("게임 재시작 실패: Game 인스턴스를 찾을 수 없습니다.", "error");
    }
  }

  // === 업그레이드 선택 시스템 ===

  // 업그레이드 선택 UI 표시
  showUpgradeSelection(options, onSelectCallback) {
    // 업그레이드 선택 화면이 이미 열려있으면 무시 (중첩 방지)
    if (this.isUpgradeSelectionOpen) {
      Utils.log("업그레이드 선택 화면이 이미 열려 있습니다. 무시합니다.");
      return;
    }

    // 혹시 남아있는 화면이 있다면 제거
    const existingScreen = document.getElementById("upgradeScreen");
    if (existingScreen) {
      existingScreen.remove();
    }

    // 업그레이드 선택 화면 열림 상태로 변경
    this.isUpgradeSelectionOpen = true;

    // 업그레이드 선택 화면 HTML 생성
    const upgradeHTML = this.createUpgradeSelectionHTML(options);
    document.body.insertAdjacentHTML("beforeend", upgradeHTML);

    // 선택 버튼 이벤트 리스너 설정
    this.setupUpgradeButtons(options, onSelectCallback);

    // 화면 표시
    const upgradeScreen = document.getElementById("upgradeScreen");
    if (upgradeScreen) {
      setTimeout(() => {
        upgradeScreen.classList.add("show");
      }, 10);
    }

    Utils.log("업그레이드 선택 화면 표시");
  }

  // 업그레이드 선택 화면 HTML 생성
  createUpgradeSelectionHTML(options) {
    const optionsHTML = options
      .map(
        (option, index) => `
      <div class="upgrade-option" data-upgrade-id="${
        option.id
      }" data-index="${index}">
        <div class="upgrade-header" style="background: linear-gradient(135deg, ${
          option.color
        }, ${this.darkenColor(option.color, 10)});">
          <div class="upgrade-class-name">${option.className}</div>
        </div>
        <div class="upgrade-body">
          <div class="upgrade-name">${option.name}</div>
          <div class="upgrade-description">${option.description}</div>
        </div>
      </div>
    `
      )
      .join("");

    return `
      <div id="upgradeScreen" class="upgrade-screen">
        <div class="upgrade-content">
          <h1 class="upgrade-title">레벨업!</h1>
          <div class="upgrade-options-container">
            ${optionsHTML}
          </div>
        </div>
      </div>
    `;
  }

  // 업그레이드 옵션 클릭 이벤트 설정
  setupUpgradeButtons(options, onSelectCallback) {
    const upgradeOptions = document.querySelectorAll(".upgrade-option");
    let isSelecting = false; // 중복 선택 방지 플래그

    upgradeOptions.forEach((optionElement) => {
      optionElement.addEventListener("click", () => {
        // 이미 선택 중이면 무시
        if (isSelecting) {
          return;
        }

        isSelecting = true;
        const index = parseInt(optionElement.dataset.index);
        const selectedOption = options[index];
        Utils.log(`업그레이드 선택: ${selectedOption.name}`);

        // 모든 옵션 비활성화 (중복 클릭 방지)
        upgradeOptions.forEach((opt) => {
          opt.style.opacity = "0.6";
          opt.style.pointerEvents = "none";
        });

        // 선택 효과
        optionElement.style.opacity = "1";
        optionElement.style.borderColor = "#4CAF50";
        optionElement.style.boxShadow = "0 0 30px rgba(76, 175, 80, 0.6)";

        // 잠시 후 화면 닫기 및 콜백 실행
        setTimeout(() => {
          this.hideUpgradeSelection();
          if (onSelectCallback) {
            onSelectCallback(selectedOption.id);
          }
          isSelecting = false; // 선택 완료 후 플래그 리셋
        }, 300);
      });
    });
  }

  // 업그레이드 선택 화면 숨기기
  hideUpgradeSelection() {
    // 업그레이드 선택 화면 닫힘 상태로 변경 (즉시 변경해서 다음 레벨업 가능하도록)
    this.isUpgradeSelectionOpen = false;

    const upgradeScreen = document.getElementById("upgradeScreen");
    if (upgradeScreen) {
      upgradeScreen.classList.remove("show");
      setTimeout(() => {
        upgradeScreen.remove();
      }, 300);
    }
  }

  // 색상 어둡게 하기 (유틸리티 함수)
  darkenColor(color, percent) {
    // hex 색상을 RGB로 변환
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // percent만큼 어둡게
    const newR = Math.max(0, Math.floor((r * (100 - percent)) / 100));
    const newG = Math.max(0, Math.floor((g * (100 - percent)) / 100));
    const newB = Math.max(0, Math.floor((b * (100 - percent)) / 100));

    // 다시 hex로 변환
    return `#${newR.toString(16).padStart(2, "0")}${newG
      .toString(16)
      .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
  }

  // 화면 효과 관련 메서드들

  // 폭탄 화면 효과 실행 (흰색 화면이 0.2초 동안 FadeOut)
  playBombScreenEffect() {
    // 요소를 다시 찾아보기 (동적으로 생성된 경우 대비)
    let overlay = this.elements.screenEffectOverlay;
    if (!overlay) {
      overlay = document.getElementById("screenEffectOverlay");
      if (overlay) {
        this.elements.screenEffectOverlay = overlay;
        Utils.log("화면 효과 오버레이를 동적으로 찾았습니다.");
      }
    }

    if (!overlay) {
      Utils.log(
        "화면 효과 오버레이를 찾을 수 없습니다. HTML에 요소가 있는지 확인해주세요.",
        "error"
      );
      return;
    }

    Utils.log("폭탄 화면 효과 실행 중...");

    // 즉시 흰색 화면 표시
    overlay.style.display = "block";
    overlay.style.opacity = "1";
    overlay.style.backgroundColor = "rgba(255, 255, 255, 1)";
    overlay.style.zIndex = "9999"; // 최상위로 설정

    // 0.2초 동안 FadeOut 애니메이션
    const startTime = Date.now();
    const duration = 500; // 0.2초

    const fadeOut = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const opacity = 1 - progress;

      overlay.style.opacity = opacity;

      if (progress < 1) {
        requestAnimationFrame(fadeOut);
      } else {
        // 애니메이션 완료
        overlay.style.display = "none";
        overlay.style.opacity = "0";
        overlay.style.zIndex = "2000"; // 원래 z-index로 복원
        Utils.log("폭탄 화면 효과 완료");
      }
    };

    // 다음 프레임에서 FadeOut 시작
    requestAnimationFrame(fadeOut);
  }

  // 화면 효과 오버레이 숨기기 (강제로)
  hideScreenEffect() {
    if (this.elements.screenEffectOverlay) {
      this.elements.screenEffectOverlay.classList.remove("active");
      this.elements.screenEffectOverlay.style.opacity = "0";
    }
  }

  // 디버깅용: 화면 효과 테스트
  testScreenEffect() {
    Utils.log("화면 효과 테스트 시작...");
    this.playBombScreenEffect();
  }

  // === 동료 로그 표시 시스템 ===

  // 동료 로그 표시 (소환/사망)
  showCompanionLog(companionName, isSpawned, companionCount) {
    // 로그 컨테이너가 없으면 생성
    let logContainer = document.getElementById("companionLogContainer");
    if (!logContainer) {
      logContainer = document.createElement("div");
      logContainer.id = "companionLogContainer";
      logContainer.className = "companion-log-container";
      const gameUI = document.getElementById("gameUI");
      if (gameUI) {
        gameUI.appendChild(logContainer);
      } else {
        document.body.appendChild(logContainer);
      }
    }

    // 로그 항목 생성
    const logItem = document.createElement("div");
    logItem.className = `companion-log-item ${isSpawned ? "spawned" : "died"}`;

    const icon = isSpawned ? "✨" : "💀";
    const message = isSpawned
      ? `${companionName} 소환! (${companionCount}명)`
      : `${companionName} 사망! (${companionCount}명)`;

    logItem.innerHTML = `
      <span class="companion-log-icon">${icon}</span>
      <span class="companion-log-text">${message}</span>
    `;

    // 컨테이너에 추가
    logContainer.appendChild(logItem);

    // 애니메이션 시작
    setTimeout(() => {
      logItem.classList.add("show");
    }, 10);

    // 3초 후 제거
    setTimeout(() => {
      logItem.classList.add("hide");
      setTimeout(() => {
        if (logItem.parentNode) {
          logItem.remove();
        }
      }, 300);
    }, 3000);
  }

  // === 스테이지 시작 표시 시스템 ===

  // 스테이지 시작 표시
  showStageStart(stageNumber, subtitle = "") {
    // 스테이지 표시 컨테이너가 없으면 생성
    let stageContainer = document.getElementById("stageStartContainer");
    if (!stageContainer) {
      stageContainer = document.createElement("div");
      stageContainer.id = "stageStartContainer";
      stageContainer.className = "stage-start-container";
      const gameUI = document.getElementById("gameUI");
      if (gameUI) {
        gameUI.appendChild(stageContainer);
      } else {
        document.body.appendChild(stageContainer);
      }
    }

    // 기존 내용 제거
    stageContainer.innerHTML = "";

    // 스테이지 번호 표시 (먼저 추가)
    const stageNumberEl = document.createElement("div");
    stageNumberEl.className = "stage-number";
    stageNumberEl.textContent = `STAGE ${stageNumber}`;
    stageContainer.appendChild(stageNumberEl);

    // 자막 표시 (있는 경우) - 번호 아래에 표시
    if (subtitle) {
      const subtitleEl = document.createElement("div");
      subtitleEl.className = "stage-subtitle";
      subtitleEl.textContent = subtitle;
      stageContainer.appendChild(subtitleEl);
    }

    // 애니메이션은 CSS에서 자동으로 시작됨
    // 2.5초 후 (애니메이션 완료 시점) 컨테이너 내용 제거
    setTimeout(() => {
      stageContainer.innerHTML = "";
    }, 2500);
  }

  // 보스 등장 카운트다운 표시 (빨간색)
  showBossCountdown(countdownNumber) {
    // 이전 카운트다운 타이머가 있으면 취소
    if (this.bossCountdownTimer) {
      clearTimeout(this.bossCountdownTimer);
      this.bossCountdownTimer = null;
    }

    // 스테이지 표시 컨테이너 재활용
    let stageContainer = document.getElementById("stageStartContainer");
    if (!stageContainer) {
      stageContainer = document.createElement("div");
      stageContainer.id = "stageStartContainer";
      stageContainer.className = "stage-start-container";
      const gameUI = document.getElementById("gameUI");
      if (gameUI) {
        gameUI.appendChild(stageContainer);
      } else {
        document.body.appendChild(stageContainer);
      }
    }

    // 기존 내용 제거
    stageContainer.innerHTML = "";

    // 카운트다운 숫자 표시 (빨간색)
    const countdownEl = document.createElement("div");
    countdownEl.className = "stage-number boss-countdown";
    countdownEl.textContent = `${countdownNumber}`;
    countdownEl.style.color = "#ff0000"; // 빨간색
    countdownEl.style.textShadow =
      "3px 3px 6px rgba(0, 0, 0, 0.8), 0 0 30px rgba(255, 0, 0, 0.8)"; // 빨간색 발광 효과
    // 카운트다운 애니메이션 적용 (빠른 펄스 효과)
    countdownEl.style.animation = "bossCountdownPulse 1s ease-out";
    stageContainer.appendChild(countdownEl);

    // 1초 후 내용 제거 (다음 카운트다운을 위해)
    this.bossCountdownTimer = setTimeout(() => {
      stageContainer.innerHTML = "";
      this.bossCountdownTimer = null;
    }, 1000);
  }
}

// webpack 빌드를 위해 default export 추가
export default UIManager;
